import { isPlatformBrowser, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  NgZone,
  OnDestroy,
  OnInit,
  output,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { TranslatePipe } from 'translate';
import { sleep } from 'utils';

@Component({
  selector: 'lib-countdown',
  templateUrl: './countdown.component.html',
  imports: [NgClass, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountdownComponent implements OnInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private ngZone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);
  public countdownDateFromPrismic = input.required<string>();
  public titleCss = input('!text-4xl !font-bold !text-center mb-0');
  public textCss = input('!text-xs !text-center');
  public showTexts = input(true);
  public endDate!: Date;
  public dateDiff!: Date;
  public hide = signal(true);
  public $timer: Subscription;
  public countdownIsOver = output();

  public async ngOnInit() {
    this.endDate = new Date(this.countdownDateFromPrismic());
    this.dateDiff = new Date(this.endDate.getTime() - new Date().getTime());

    if (isPlatformBrowser(this.platformId)) {
      // Cannot let this run on the server as it will then run indefinitely (and never return to the client)
      this.ngZone.runOutsideAngular(() => {
        this.$timer = timer(0, 1000).subscribe(() => {
          const hideCountDown = new Date(this.countdownDateFromPrismic()).getTime() - new Date().getTime() <= 0;
          if (hideCountDown) {
            this.hide.set(true);
            this.countdownIsOver.emit();
          }
          this.dateDiff = new Date(this.endDate.getTime() - new Date().getTime());
          this.ngZone.run(() => this.cdr.markForCheck());
        });
      });

      await sleep(0);
      this.hide.set(false);
      this.cdr.markForCheck();
    }
  }

  public ngOnDestroy(): void {
    this.$timer?.unsubscribe();
  }

  public get days(): number {
    return this.dateDiff.getUTCDate() - 1;
  }

  public get hours(): number {
    return this.dateDiff.getUTCHours();
  }

  public get minutes(): number {
    return this.dateDiff.getUTCMinutes();
  }

  public get seconds(): number {
    return this.dateDiff.getUTCSeconds();
  }

  public get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }
}
